/**
 * Consent resolution helpers.
 * Order of precedence:
 * stored state -> config defaults -> GPC/DNT -> Shopify -> TCF.
 *
 * Additional signals (recorded as metadata only):
 * - US Privacy (`__uspapi`)
 * - IAB GPP (`__gpp`)
 */
import type { ConsentSnapshot, ConsentState, PixelConfig } from "./types";
import { safeNavigator } from "./utils";

export type StoredConsent = {
  state: ConsentState;
  updatedAt: number;
};

export function readStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed && parsed.state) return parsed;
  } catch {
    return null;
  }
  return null;
}

function readDnt(): boolean {
  const nav = safeNavigator();
  const win = typeof window === "undefined" ? undefined : (window as any);
  const value = nav?.doNotTrack ?? win?.doNotTrack;
  return value === "1" || value === "yes";
}

function readGpc(): boolean {
  const nav = safeNavigator() as
    | (Navigator & { globalPrivacyControl?: boolean })
    | undefined;
  return Boolean(nav?.globalPrivacyControl);
}

function readShopifyConsent(): { state?: ConsentState; raw?: unknown } {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const shopify = win?.Shopify?.customerPrivacy;
  if (!shopify) return {};

  try {
    if (typeof shopify.getTrackingConsent === "function") {
      const consent = shopify.getTrackingConsent();
      if (consent === true) return { state: "granted", raw: consent };
      if (consent === false) return { state: "denied", raw: consent };
    }
    if (typeof shopify.analyticsProcessingAllowed === "function") {
      const allowed = shopify.analyticsProcessingAllowed();
      return { state: allowed ? "granted" : "denied", raw: allowed };
    }
  } catch {
    return {};
  }

  return {};
}

function readUspConsent(): Promise<{ uspString?: string; raw?: unknown }> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const uspapi = win?.__uspapi;
  if (!uspapi) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      uspapi("getUSPData", 1, (uspData: any, success: boolean) => {
        if (!success || !uspData) return resolve({});
        const uspString =
          typeof uspData.uspString === "string" ? uspData.uspString : undefined;
        return resolve({ uspString, raw: uspData });
      });
    } catch {
      resolve({});
    }
  });
}

function readGppConsent(): Promise<{
  gppString?: string;
  sectionIds?: number[];
  raw?: unknown;
}> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const gpp = win?.__gpp;
  if (!gpp) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      gpp("getGPPData", (gppData: any, success: boolean) => {
        if (!success || !gppData) return resolve({});
        const gppString =
          typeof gppData.gppString === "string" ? gppData.gppString : undefined;
        const sectionIds = Array.isArray(gppData.applicableSections)
          ? gppData.applicableSections
              .map((value: unknown) => Number(value))
              .filter((value: number) => Number.isFinite(value))
          : undefined;
        return resolve({
          gppString,
          sectionIds: sectionIds?.length ? sectionIds : undefined,
          raw: gppData,
        });
      });
    } catch {
      resolve({});
    }
  });
}

function readTcfConsent(): Promise<{
  state?: ConsentState;
  tcString?: string;
  raw?: unknown;
}> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const tcfapi = win?.__tcfapi;
  if (!tcfapi) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      tcfapi("getTCData", 2, (tcData: any, success: boolean) => {
        if (!success || !tcData) return resolve({});
        if (tcData.gdprApplies === false) {
          return resolve({
            state: "granted",
            tcString: tcData.tcString,
            raw: tcData,
          });
        }
        const consented = Boolean(tcData?.purpose?.consents?.[1]);
        return resolve({
          state: consented ? "granted" : "denied",
          tcString: tcData.tcString,
          raw: tcData,
        });
      });
    } catch {
      resolve({});
    }
  });
}

export async function resolveConsent(
  config: PixelConfig,
  stored: StoredConsent | null,
): Promise<ConsentSnapshot> {
  let state: ConsentState = stored?.state ?? config.privacy.defaultConsent;
  const gpc = readGpc();
  const dnt = readDnt();

  // GPC takes precedence when enabled.
  if (config.privacy.respectGpc && gpc) {
    state = config.privacy.gpcDefault;
  } else if (config.privacy.respectDnt && dnt && state === "unknown") {
    state = "denied";
  }

  const shopify = readShopifyConsent();
  if (shopify.state && state === "unknown") {
    state = shopify.state;
  }

  const [tcf, usp, gpp] = await Promise.all([
    readTcfConsent(),
    readUspConsent(),
    readGppConsent(),
  ]);
  if (tcf.state && state === "unknown") {
    state = tcf.state;
  }

  const includeRaw = Boolean(config.debug);
  const hasCmpSignals = Boolean(
    tcf.tcString || shopify.state || usp.uspString || gpp.gppString,
  );
  const cmp = hasCmpSignals
    ? {
        source: tcf.tcString
          ? "tcf"
          : shopify.state
            ? "shopify"
            : gpp.gppString
              ? "gpp"
              : usp.uspString
                ? "usp"
                : "unknown",
        tcString: tcf.tcString,
        uspString: usp.uspString,
        gppString: gpp.gppString,
        gppSectionIds: gpp.sectionIds,
        ...(includeRaw
          ? {
              raw: {
                ...(tcf.raw ? { tcf: tcf.raw } : {}),
                ...(shopify.raw ? { shopify: shopify.raw } : {}),
                ...(usp.raw ? { usp: usp.raw } : {}),
                ...(gpp.raw ? { gpp: gpp.raw } : {}),
              },
            }
          : {}),
      }
    : undefined;

  return { state, gpc, dnt, cmp };
}
